import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { getData, putData } from '../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const token = () => {
  const raw = sessionStorage.getItem('commonToken') || sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const toLocalInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Turns the whole customer API (website and mobile app) off with a message. Only a super admin can change it.
const MaintenanceControl = ({ canEdit }) => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ enabled: false, title: '', message: '', estimatedEnd: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmOn, setConfirmOn] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getData('maintenance', null, token(), false, '', true);
      if (res?.success) { setState(res.maintenance); setLoadFailed(false); } else setLoadFailed(true);
    } catch { setLoadFailed(true); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const show = () => {
    setForm({ enabled: !!state?.enabled, title: state?.title || '', message: state?.message || '', estimatedEnd: toLocalInput(state?.estimatedEnd) });
    setError('');
    setConfirmOn(false);
    setOpen(true);
  };

  // switching off needs no extra input; switching on opens the dialog so the message is checked and confirmed
  const quickToggle = async (checked) => {
    if (checked) { show(); setForm((f) => ({ ...f, enabled: true })); return; }
    setSaving(true);
    try {
      const res = await putData('maintenance', { enabled: false, title: state?.title, message: state?.message, estimatedEnd: null }, token());
      if (res?.maintenance) setState(res.maintenance);
    } catch (e) {
      load();
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (form.enabled && !state?.enabled && !confirmOn) { setConfirmOn(true); return; }
    setSaving(true);
    setError('');
    try {
      const res = await putData('maintenance', {
        enabled: form.enabled,
        title: form.title,
        message: form.message,
        estimatedEnd: form.estimatedEnd ? new Date(form.estimatedEnd).toISOString() : null,
      }, token());
      if (res?.maintenance) setState(res.maintenance);
      setOpen(false);
    } catch (e) {
      setError(e?.message || 'Could not save maintenance mode.');
      setConfirmOn(false);
    } finally {
      setSaving(false);
    }
  };

  const on = !!state?.enabled;
  const ready = !!state && !loadFailed;

  return (
    <>
      <Tooltip title={loadFailed ? 'Could not reach the maintenance API. Restart the backend and refresh.' : on ? 'The website and app are closed. Switch off to reopen.' : canEdit ? 'Switch on to close the website and app for maintenance.' : 'Maintenance mode is off. Only a super admin can change it.'}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.25, pr: 0.5, height: 38, borderRadius: '10px', fontFamily: FONT,
            background: on ? '#fee2e2' : '#f1f5f9', border: `1px solid ${on ? '#fca5a5' : '#e2e8f0'}`,
            ...(on ? { animation: 'mmPulse 1.6s ease-in-out infinite', '@keyframes mmPulse': { '50%': { opacity: 0.65 } } } : {}),
          }}
        >
          <Typography onClick={canEdit && ready ? show : undefined} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12, color: on ? '#991b1b' : '#475569', cursor: canEdit ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
            {on ? 'Maintenance ON' : 'Maintenance'}
          </Typography>
          <Switch size="small" color="error" checked={on} disabled={!canEdit || !ready || saving} onChange={(e) => quickToggle(e.target.checked)} />
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Maintenance mode</DialogTitle>
        <DialogContent>
          <Alert severity={form.enabled ? 'warning' : 'info'} sx={{ mb: 2, fontFamily: FONT }}>
            While this is on, every website and mobile app request gets a “maintenance” response and nobody can browse, sign in or order. The admin panel keeps working, and payment and courier callbacks are still received.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', background: form.enabled ? '#fef2f2' : '#f8fafc' }}>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800 }}>{form.enabled ? 'Shop is closed' : 'Shop is open'}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>
                {state?.enabledAt && state?.enabled ? `Closed since ${new Date(state?.enabledAt).toLocaleString('en-IN')}${state?.updatedByName ? ` by ${state?.updatedByName}` : ''}` : 'Switch on to close the website and app'}
              </Typography>
            </Box>
            <Switch checked={form.enabled} onChange={(e) => { setForm({ ...form, enabled: e.target.checked }); setConfirmOn(false); }} color="error" />
          </Box>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField size="small" label="Title customers see" value={form.title} inputProps={{ maxLength: 120 }} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="We will be back soon" />
            <TextField size="small" label="Message customers see *" value={form.message} multiline minRows={3} inputProps={{ maxLength: 600 }} onChange={(e) => setForm({ ...form, message: e.target.value })}
              helperText={`${form.message.length}/600`} />
            <TextField size="small" type="datetime-local" label="Expected back at (optional)" value={form.estimatedEnd} onChange={(e) => setForm({ ...form, estimatedEnd: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Box>
          {confirmOn && <Alert severity="error" sx={{ mt: 2, fontFamily: FONT }}>This closes the website and the app for every customer right now. Press “Yes, close the shop” to confirm.</Alert>}
          {error && <Alert severity="error" sx={{ mt: 2, fontFamily: FONT }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained" color={form.enabled ? 'error' : 'primary'} sx={{ textTransform: 'none', fontWeight: 800, ...(form.enabled ? {} : { background: 'rgb(255 209 2)', color: '#000' }) }}>
            {saving ? <CircularProgress size={18} /> : confirmOn ? 'Yes, close the shop' : form.enabled && !state?.enabled ? 'Turn on' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MaintenanceControl;
